import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { X } from 'lucide-react';

const FALLBACK_FRAME_HEIGHT = 3600;

interface TravelKitPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** The exact link the client will receive — not a rebuilt copy of it. */
  url: string;
  labels: {
    title: string;
    hint: string;
    close: string;
  };
}

/**
 * The SOS page as the client will see it, in a phone-sized frame, before the
 * adviser sends it — so a wrong country, insurer or language is caught here
 * rather than by the client.
 *
 * **Look, don't touch.** The frame loads the real page, and the real page dials
 * 110, opens WhatsApp, asks for location and sounds an alarm. Guarding each of
 * those inside an iframe differs between browsers, iOS WKWebView and Android
 * WebView (Capacitor launches `tel:` from a subframe on Android), so the frame
 * receives no input at all: `pointer-events: none` and out of the tab order.
 * The wrapper around it scrolls instead, over a frame tall enough to hold the
 * whole page. Nothing inside can be pressed, on any platform.
 *
 * The SOS page skips its welcome sheet when framed (`App.tsx`) — it would
 * otherwise cover the page with a close button nobody can press.
 */
// `sos-paper-height` is the pre-rename name. The page (travelkit/src/App.tsx) sends both names
// so web builds released before the rename keep resizing their preview; this web build accepts both
// so it also works against a page build that has not been redeployed yet. Drop `sos-paper-height`
// here and in App.tsx together, once no web or native build older than the rename is still in use.
const HEIGHT_MESSAGE_TYPES = ['travelkit-height', 'sos-paper-height'];

const TravelKitPreviewDialog: React.FC<TravelKitPreviewDialogProps> = ({ open, onClose, url, labels }) => {
  const fullScreen = useMediaQuery('(max-width: 600px)');
  // The page posts its own height (`travelkit-height`, App.tsx; the old
  // `sos-paper-height` is still accepted while an older page build is live) so the frame
  // ends where the page does. Accepted only from the SOS origin; until it
  // arrives, a height that holds the whole page.
  const [frameHeight, setFrameHeight] = useState(FALLBACK_FRAME_HEIGHT);

  useEffect(() => {
    if (!open) return undefined;
    setFrameHeight(FALLBACK_FRAME_HEIGHT);
    let travelKitOrigin = '';
    try {
      travelKitOrigin = new URL(url).origin;
    } catch {
      return undefined;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== travelKitOrigin || !HEIGHT_MESSAGE_TYPES.includes(event.data?.type)) return;
      const height = Number(event.data.height);
      if (Number.isFinite(height) && height > 0) setFrameHeight(Math.min(Math.ceil(height), 8000));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      className="travelkit-preview-dialog"
      aria-labelledby="travelkit-preview-title"
    >
      <div className="travelkit-preview-head">
        <h4 id="travelkit-preview-title">{labels.title}</h4>
        <button
          type="button"
          className="travelkit-preview-icon-btn"
          onClick={onClose}
          aria-label={labels.close}
          title={labels.close}
        >
          <X size={16} />
        </button>
      </div>
      <p className="travelkit-preview-hint">{labels.hint}</p>
      <div className="travelkit-preview-stage">
        <div className="travelkit-preview-phone">
          {open && (
            <iframe
              src={url}
              title={labels.title}
              tabIndex={-1}
              aria-hidden="true"
              style={{ height: frameHeight }}
              data-testid="travelkit-preview-frame"
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default TravelKitPreviewDialog;
